import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { ChevronRightIcon } from '@/components/atoms/icons'

export type GoalCardLayout = 'icon-top' | 'icon-right'
export type GoalCardSize = 'sm' | 'md' | 'lg'

export type GoalCardLink = { label: string } & (
  | { to: string; onClick?: never }
  | { onClick: () => void; to?: never }
)

export interface GoalCardProps {
  icon: string
  description: ReactNode
  layout?: GoalCardLayout
  size?: GoalCardSize
  link?: GoalCardLink
  className?: string
  testId?: string
}

const SIZES: Record<GoalCardSize, string> = {
  sm: 'min-h-27',
  md: 'min-h-35.5',
  lg: 'min-h-41',
}

const LAYOUTS: Record<GoalCardLayout, string> = {
  'icon-top': 'flex-col items-start justify-between gap-2 p-4',
  'icon-right': 'flex-row items-center gap-0.5 px-5 py-4.5',
}

const DESCRIPTION_CLASS =
  'text-sm leading-4.5 text-balance text-neutral-900 [&_strong]:font-bold'

const LINK_CLASS =
  'tracking-base text-indigo-base flex items-center gap-1 text-base leading-4 hover:underline'

export function GoalCard({
  icon,
  description,
  layout = 'icon-top',
  size = 'sm',
  link,
  className = '',
  testId,
}: GoalCardProps) {
  const isIconRight = layout === 'icon-right'
  const iconImage = (
    <img
      src={icon}
      alt=""
      aria-hidden
      className={`shrink-0 ${isIconRight ? 'size-12.5' : 'size-10'}`}
    />
  )

  return (
    <div
      className={`flex overflow-clip rounded-lg border border-yellow-100 bg-yellow-50 ${LAYOUTS[layout]} ${SIZES[size]} ${className}`}
      data-testid={testId}
    >
      {!isIconRight && iconImage}
      <div className={isIconRight ? 'flex flex-1 flex-col gap-2' : 'contents'}>
        <p className={DESCRIPTION_CLASS}>{description}</p>
        {link && <GoalCardLinkButton link={link} testId={testId} />}
      </div>
      {isIconRight && iconImage}
    </div>
  )
}

function GoalCardLinkButton({
  link,
  testId,
}: {
  link: GoalCardLink
  testId?: string
}) {
  const content = (
    <>
      {link.label}
      <ChevronRightIcon className="size-4" />
    </>
  )
  const linkTestId = testId && `${testId}-link`

  return link.to ? (
    <Link to={link.to} className={LINK_CLASS} data-testid={linkTestId}>
      {content}
    </Link>
  ) : (
    <button
      type="button"
      onClick={link.onClick}
      className={LINK_CLASS}
      data-testid={linkTestId}
    >
      {content}
    </button>
  )
}
